// Permission utility functions for frontend

// Check if user has a specific permission
export const hasPermission = (user, permissionName) => {
  if (!user || !user.permissions) return false;
  
  // Super admins have all permissions
  if (user.role === 'super-admin') return true;
  
  // Check if user has the specific permission
  return user.permissions.includes(permissionName);
};

// Check if user has any of the specified permissions
export const hasAnyPermission = (user, permissionNames) => {
  if (!user || !user.permissions) return false;
  
  // Super admins have all permissions
  if (user.role === 'super-admin') return true;
  
  // Check if user has any of the permissions
  return permissionNames.some(permission => user.permissions.includes(permission));
};

// Check if user has all of the specified permissions
export const hasAllPermissions = (user, permissionNames) => {
  if (!user || !user.permissions) return false;
  
  // Super admins have all permissions
  if (user.role === 'super-admin') return true;
  
  // Check if user has all of the permissions
  return permissionNames.every(permission => user.permissions.includes(permission));
};

// Check if user is admin or super admin
export const isAdmin = (user) => {
  if (!user) return false;
  return user.role === 'admin' || user.role === 'super-admin';
};

// Check if user is super admin
export const isSuperAdmin = (user) => {
  if (!user) return false;
  return user.role === 'super-admin';
};

// Get user's role display name
export const getRoleDisplayName = (role) => {
  const roleNames = {
    'visitor': 'Visitor',
    'member': 'Member',
    'admin': 'Admin',
    'super-admin': 'Super Admin'
  };
  return roleNames[role] || 'Visitor';
};

// Get voice part display name and icon
export const getVoicePartDisplay = (voicePart) => {
  const voiceParts = {
    'soprano': { name: 'Soprano', icon: '🎵', color: '#ef4444' },
    'alto': { name: 'Alto', icon: '🎶', color: '#f97316' },
    'tenor': { name: 'Tenor', icon: '🎼', color: '#3b82f6' },
    'bass': { name: 'Bass', icon: '🎤', color: '#8b5cf6' }
  };
  return voiceParts[voicePart] || { name: 'Not specified', icon: '👤', color: '#6b7280' };
};

// Get all available voice parts
export const getVoiceParts = () => {
  return [
    { value: 'soprano', label: 'Soprano', icon: '🎵' },
    { value: 'alto', label: 'Alto', icon: '🎶' },
    { value: 'tenor', label: 'Tenor', icon: '🎼' },
    { value: 'bass', label: 'Bass', icon: '🎤' }
  ];
};

// Get permission display name
export const getPermissionDisplayName = (permission) => {
  const permissionNames = {
    'use-player': 'Use XML Player',
    'save-settings': 'Save Settings',
    'manage-xml-files': 'Manage XML Files',
    'manage-users': 'Manage Users',
    'manage-permissions': 'Manage Permissions',
    'manage-tips': 'Manage Tips',
    'manage-news': 'Manage News',
    'update-news': 'Update News',
    'manage-rehearsals': 'Manage Rehearsals',
    'manage-issues': 'Manage Issues',
    'manage-gallery': 'Manage Gallery',
    'manage-telegram-bot': 'Manage Telegram Bot',
    'manage-settings': 'Manage Settings',
    'view-analytics': 'View Analytics',
    'view-activity-logs': 'View Activity Logs',
    'manage-calendar': 'Manage Calendar',
    'manage-vocal-exercises': 'Manage Vocal Exercises'
  };
  return permissionNames[permission] || permission;
};

// Permission categories for UI organization
export const PERMISSION_CATEGORIES = {
  basic: {
    name: 'Basic Features',
    permissions: ['use-player', 'save-settings']
  },
  content: {
    name: 'Content Management',
    permissions: ['manage-xml-files', 'manage-tips', 'manage-news', 'update-news', 'manage-rehearsals', 'manage-gallery', 'manage-calendar', 'manage-vocal-exercises']
  },
  admin: {
    name: 'Administration',
    permissions: ['manage-users', 'manage-permissions', 'manage-issues', 'manage-telegram-bot', 'manage-settings', 'view-analytics', 'view-activity-logs']
  }
};

// Get all permissions organized by category
export const getPermissionsByCategory = () => {
  return PERMISSION_CATEGORIES;
};

// Check if user can perform an action
export const canPerformAction = (user, action) => {
  const actionPermissions = {
    // Player actions
    'play-music': ['use-player'],
    'load-file': ['use-player'],
    'save-preset': ['save-settings'],
    
    // File management
    'manage-storage': ['manage-xml-files'],
    'upload-file': ['manage-xml-files'],
    'delete-file': ['manage-xml-files'],
    'download-file': ['manage-xml-files'],
    
    // User management
    'edit-user': ['manage-users'],
    'grant-permission': ['manage-permissions'],
    'revoke-permission': ['manage-permissions'],
    
    // Content management
    'create-news': ['manage-news'],
    'edit-news': ['manage-news'],
    'delete-news': ['manage-news'],
    'publish-news': ['manage-news'],
    
    // Tips management
    'create-tip': ['manage-tips'],
    'edit-tip': ['manage-tips'],
    'delete-tip': ['manage-tips'],
    'view-tips': ['manage-tips'],
    
    // Rehearsal management
    'create-rehearsal': ['manage-rehearsals'],
    'edit-rehearsal': ['manage-rehearsals'],
    'delete-rehearsal': ['manage-rehearsals'],
    'manage-rehearsal-locations': ['manage-rehearsals'],
    'schedule-rehearsal': ['manage-rehearsals'],
    
    // Issue management
    'view-issues': ['manage-issues'],
    'edit-issue': ['manage-issues'],
    'assign-issue': ['manage-issues'],
    'resolve-issue': ['manage-issues'],
    'delete-issue': ['manage-issues'],
    
    // Gallery management
    'upload-gallery-image': ['manage-gallery'],
    'edit-gallery-item': ['manage-gallery'],
    'delete-gallery-item': ['manage-gallery'],
    'manage-gallery-categories': ['manage-gallery'],
    'feature-gallery-item': ['manage-gallery'],
    
    // Analytics
    'view-metrics': ['view-analytics'],
    'export-data': ['view-analytics'],
    
    // Calendar management
    'manage-calendar': ['manage-calendar'],
    'create-calendar-entry': ['manage-calendar'],
    'edit-calendar-entry': ['manage-calendar'],
    'delete-calendar-entry': ['manage-calendar']
  };
  
  const requiredPermissions = actionPermissions[action];
  if (!requiredPermissions) return false;
  
  return hasAllPermissions(user, requiredPermissions);
};

// API call to check permission on server
export const checkPermissionAPI = async (userId, permission) => {
  try {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(
      `${API_BASE}/api/permissions?operation=checkPermission&userId=${userId}&permission=${permission}`
    );
    
    if (response.ok) {
      const data = await response.json();
      return data.hasPermission;
    }
    return false;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

// API call to list all permissions (admin only)
export const listPermissionsAPI = async (userId) => {
  try {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(
      `${API_BASE}/api/permissions?operation=listPermissions&userId=${userId}`
    );
    
    if (response.ok) {
      const data = await response.json();
      return data.permissions;
    }
    return [];
  } catch (error) {
    console.error('Error listing permissions:', error);
    return [];
  }
};

// API call to grant permission (admin only)
export const grantPermissionAPI = async (userId, targetUserId, permission) => {
  try {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(
      `${API_BASE}/api/permissions?operation=grantPermission&userId=${userId}&targetUserId=${targetUserId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permission })
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      return { success: true, message: data.message };
    } else {
      const error = await response.json();
      return { success: false, error: error.error };
    }
  } catch (error) {
    console.error('Error granting permission:', error);
    return { success: false, error: 'Network error' };
  }
};

// API call to revoke permission (admin only)
export const revokePermissionAPI = async (userId, targetUserId, permission) => {
  try {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(
      `${API_BASE}/api/permissions?operation=revokePermission&userId=${userId}&targetUserId=${targetUserId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permission })
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      return { success: true, message: data.message };
    } else {
      const error = await response.json();
      return { success: false, error: error.error };
    }
  } catch (error) {
    console.error('Error revoking permission:', error);
    return { success: false, error: 'Network error' };
  }
};